// AES-CMAC (RFC 4493) and protovision's KDF on top of WebCrypto (WEB-04). WebCrypto has AES but no CMAC; AES-CBC
// with a zero IV gives the CBC-MAC chain, and CMAC is that chain with the last block masked by the subkey K1 or K2.
// Tested against the RFC 4493 vectors and protovision's claim-proof vector.

const ZERO = new Uint8Array(16);

async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== 16) throw new Error("AES-128 key must be 16 bytes");
  return crypto.subtle.importKey("raw", raw as BufferSource, { name: "AES-CBC" }, false, ["encrypt"]);
}

/** CBC encryption with a zero IV, without the padding block WebCrypto appends. */
async function cbcChain(key: CryptoKey, data: Uint8Array): Promise<Uint8Array> {
  const out = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-CBC", iv: ZERO as BufferSource }, key, data as BufferSource),
  );
  return out.slice(0, data.length);
}

const at = (b: Uint8Array, i: number): number => b[i] ?? 0;

function shiftLeft(block: Uint8Array): Uint8Array {
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = ((at(block, i) << 1) | (i < 15 ? at(block, i + 1) >> 7 : 0)) & 0xff;
  if (at(block, 0) & 0x80) out[15] = at(out, 15) ^ 0x87;
  return out;
}

export async function aesCmac(rawKey: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const key = await importKey(rawKey);
  const l = await cbcChain(key, ZERO);
  const k1 = shiftLeft(l);
  const k2 = shiftLeft(k1);

  const n = Math.max(1, Math.ceil(message.length / 16));
  const complete = message.length > 0 && message.length % 16 === 0;
  const m = new Uint8Array(n * 16);
  m.set(message);
  if (!complete) m[message.length] = 0x80;
  const mask = complete ? k1 : k2;
  for (let i = 0; i < 16; i++) m[(n - 1) * 16 + i] = at(m, (n - 1) * 16 + i) ^ at(mask, i);

  const chain = await cbcChain(key, m);
  return chain.slice((n - 1) * 16);
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

const ascii = (s: string) => new TextEncoder().encode(s);

/** protovision's KDF: SP 800-108 counter mode with CMAC, one 128-bit block (00000001 || label || 00 || ctx || 00000080). */
export function kdf(key: Uint8Array, label: string, context: Uint8Array): Promise<Uint8Array> {
  return aesCmac(
    key,
    concat(new Uint8Array([0, 0, 0, 1]), ascii(label), new Uint8Array([0]), context, new Uint8Array([0, 0, 0, 0x80])),
  );
}

/**
 * The claim MAC of VisionPlatform contracts/claim-proof.md 0.1.0:
 *   K_claim = KDF(K_local, "pv claim", len(ID) || ID)
 *   mac     = AES-CMAC(K_claim, "pv-claim-v1" || ID || lgen || nonce)
 */
export async function claimMac(
  kLocal: Uint8Array,
  deviceId: Uint8Array,
  lgen: number,
  nonce: Uint8Array,
): Promise<Uint8Array> {
  if (lgen < 0 || lgen > 255) throw new Error("lgen is one byte");
  const kClaim = await kdf(kLocal, "pv claim", concat(new Uint8Array([deviceId.length]), deviceId));
  return aesCmac(kClaim, concat(ascii("pv-claim-v1"), deviceId, new Uint8Array([lgen]), nonce));
}
